import { useQuery } from 'react-query';
import dayjs from 'dayjs';
import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { API_QUERIES, APP_ROUTES } from '@/constants';
import {
  PendingSessionViewModel,
  BookingSessionViewModel,
  api,
  createUpcomingSessionData,
  removeTime,
} from '@/common/utils';
import { FeedbackModal } from '@/common/components/feedback-modal';
import { CenteredSpinner, Title } from '@/common/components';
import { BookingCalendarEventStatus } from '@/common/api/wisoryApiClient';
import { RebuyModal } from '@/common/components/rebuy-modal';
import { DetailsSessionDialog } from '../details-session-dialog';
import { PendingSessionDialog } from '../pending-session-dialog';
import { UpcomingSessionItem } from '../upcoming-session-item';
import { PendingSessionItem } from '../pending-session-item';
import styles from './styles.module.scss';

type Props = {
  isAdvisor?: boolean;
};

export const UpcomingSessionBlock = ({ isAdvisor }: Props) => {
  const { t } = useTranslation();
  const [selectedSession, setSelectedSesson] = useState<BookingSessionViewModel>();
  const [dialogAction, setDialogAction] = useState<
    'view-session' | 'view-feedback' | 'view-rebuy'
  >();
  const [selectedRequest, setSelectedRequest] = useState<PendingSessionViewModel>();
  const today = removeTime(dayjs());
  const sessionQuery = useQuery({
    queryKey: [API_QUERIES.UPCOMING_SESSIONS, { periodStartDate: today.format('YYYY-MM-DD') }],
    queryFn: () =>
      api.baseAPI.userProfile.getCurrentUserProfileSessions({
        periodStartDate: today.toISOString(),
        pageSize: 100,
      }),
  });
  const interestRequestsQuery = useQuery({
    queryKey: isAdvisor
      ? [API_QUERIES.ADVISOR_GET_INTEREST_REQUEST]
      : [API_QUERIES.CUSTOMER_GET_INTEREST_REQUEST],
    queryFn: () =>
      isAdvisor
        ? api.pingpongApi.advisorProfile.getAdvisorInterestRequests()
        : api.pingpongApi.customerProfile.getCustomerInterestRequests(),
  });

  const data = useMemo(() => {
    if (!(sessionQuery.isFetched && interestRequestsQuery.isFetched)) {
      return {
        items: [],
        count: 0,
      };
    }
    const result = createUpcomingSessionData({
      // @ts-ignore Wrong Type of swagger generator
      sessions: sessionQuery.data?.items || [],
      requests: interestRequestsQuery.data?.items || [],
      isAdvisor: isAdvisor || false,
    });

    return {
      items: [...result.confirmedSessions, ...result.cancelledSessions, ...result.pendingSessions],
      count: result.confirmedSessions.length + result.pendingSessions.length, // Count only confirmed and pending sessions
    };
  }, [
    interestRequestsQuery.data?.items,
    interestRequestsQuery.isFetched,
    isAdvisor,
    // @ts-ignore
    sessionQuery.data?.items,
    sessionQuery.isFetched,
  ]);

  let content = <div />;

  if (sessionQuery.isLoading || interestRequestsQuery.isLoading) {
    content = (
      <div style={{ height: 100 }}>
        <CenteredSpinner />
      </div>
    );
  } else {
    if (!data.items.length) {
      content = (
        <div className={styles.emptyUpcomingSessions}>
          <span>{t('home.noUpcomingSession')}</span>
        </div>
      );
    } else {
      content = (
        <>
          {data.items.map((session, index) => {
            if ('request' in session) {
              return (
                <PendingSessionItem
                  key={session.id}
                  item={session}
                  status={BookingCalendarEventStatus.PENDING}
                  onSelect={setSelectedRequest}
                  isAdvisor={isAdvisor || false}
                />
              );
            }

            return (
              <UpcomingSessionItem
                key={session.id}
                item={session}
                status={session.status}
                onSelect={(s) => {
                  setDialogAction('view-session');
                  setSelectedSesson(s);
                }}
                // onRebuyBtnClick={(s) => {
                //   setDialogAction('view-rebuy');
                //   setSelectedSesson(s);
                // }}
                className={classNames({
                  [styles.firstItem]: index === 0,
                })}
                isAdvisor={isAdvisor || false}
              />
            );
          })}
        </>
      );
    }
  }

  return (
    <div className={styles.wrapper}>
      <Title className={styles.title} type="large">
        {t('home.upcomingSessionsTitle')} {data.count > 0 && <span>{`(${data.count})`}</span>}
      </Title>
      <div className={styles.bottomLine} />
      <div className={styles.contentWrapper}>{content}</div>
      {dialogAction === 'view-session' && selectedSession && (
        <DetailsSessionDialog
          session={selectedSession}
          onCancel={() => {
            setSelectedSesson(undefined);
            setDialogAction(undefined);
          }}
          isAdvisor={isAdvisor!}
        />
      )}
      {selectedRequest && (
        <PendingSessionDialog
          request={selectedRequest.request}
          onCancel={() => setSelectedRequest(undefined)}
          isAdvisor={isAdvisor}
        />
      )}
      {dialogAction === 'view-feedback' && selectedSession && (
        <FeedbackModal
          session={selectedSession}
          onCancel={() => {
            setSelectedSesson(undefined);
            setDialogAction(undefined);
          }}
        />
      )}
      {dialogAction === 'view-rebuy' && selectedSession && (
        <RebuyModal
          session={selectedSession}
          onCancel={() => {
            setSelectedSesson(undefined);
            setDialogAction(undefined);
          }}
        />
      )}
      <div className={styles.sessionHistoryWrapper}>
        <Link className={styles.link} to={APP_ROUTES.MY_SESSIONS}>
          {t('sessionHistory.title')}
        </Link>
      </div>
    </div>
  );
};
